import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { theme } from '../../../../config/theme';

const PageContainer = styled.div`
  min-height: 100vh;
  background: ${theme.colors.primary.background};
  padding-top: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SuccessContainer = styled.div`
  max-width: 600px;
  margin: ${theme.spacing.xl} auto;
  padding: ${theme.spacing.xl};
  text-align: center;
`;

const SuccessIcon = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto ${theme.spacing.lg};
  border-radius: 50%;
  background: ${theme.colors.accent.success};
  display: flex;
  align-items: center;
  justify-content: center;
  
  &::before {
    content: '✓';
    color: ${theme.colors.primary.background};
    font-size: 48px;
    font-weight: bold;
  }
`;

const Title = styled.h1`
  font-family: ${theme.typography.primary.fontFamily};
  font-size: ${theme.typography.primary.heading.fontSize};
  font-weight: 600;
  color: ${theme.colors.primary.text};
  margin-bottom: ${theme.spacing.md};
`;

const Message = styled.p`
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: ${theme.typography.secondary.body.fontSize};
  color: ${theme.colors.neutral.medium};
  margin-bottom: ${theme.spacing.lg};
  line-height: 1.6;
`;

const TransactionDetails = styled.div`
  background: ${theme.colors.neutral.lighter};
  border-radius: 8px;
  padding: ${theme.spacing.lg};
  margin: ${theme.spacing.xl} 0;
  text-align: left;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${theme.spacing.sm};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailLabel = styled.span`
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: ${theme.typography.secondary.body.smallFontSize};
  color: ${theme.colors.neutral.medium};
`;

const DetailValue = styled.span`
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: ${theme.typography.secondary.body.fontSize};
  font-weight: 500;
  color: ${theme.colors.primary.text};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  justify-content: center;
  margin-top: ${theme.spacing.xl};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column;
    
    button {
      width: 100%;
    }
  }
`;

const Button = styled.button`
  font-family: ${theme.typography.button.fontFamily};
  font-size: ${theme.typography.button.fontSize};
  font-weight: ${theme.typography.button.fontWeight};
  letter-spacing: ${theme.typography.button.letterSpacing};
  text-transform: ${theme.typography.button.textTransform};
  
  background: transparent;
  color: ${theme.colors.primary.buttons};
  border: 1px solid ${theme.colors.primary.buttons};
  border-radius: 50px;
  
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  cursor: pointer;
  transition: ${theme.transitions.default};
  text-decoration: none;
  
  &:hover {
    background: ${theme.colors.primary.buttons};
    color: ${theme.colors.primary.background};
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const PrimaryButton = styled(Button)`
  background: ${theme.colors.primary.buttons};
  color: ${theme.colors.primary.background};
  
  &:hover {
    background: transparent;
    color: ${theme.colors.primary.buttons};
  }
`;

const CheckoutSuccessPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { transactionId, amount } = location.state || {};

  useEffect(() => {
    // If no transaction data, redirect to home
    if (!transactionId) {
      navigate('/');
    }
  }, [transactionId, navigate]);

  const formatCurrency = (cents) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  };

  const formatTransactionId = (id) => {
    if (!id) return '';
    // Show first 8 characters and last 4 characters
    if (id.length > 12) {
      return `${id.substring(0, 8)}...${id.substring(id.length - 4)}`;
    }
    return id;
  };

  if (!transactionId) {
    return null;
  }

  return (
    <PageContainer>
      <SuccessContainer>
        <SuccessIcon />
        <Title>Payment Successful!</Title>
        <Message>
          Your payment has been processed successfully. You will receive a confirmation 
          email shortly with your receipt and purchase details.
        </Message>

        <TransactionDetails>
          <DetailRow>
            <DetailLabel>Transaction ID</DetailLabel>
            <DetailValue>{formatTransactionId(transactionId)}</DetailValue>
          </DetailRow>
          {amount && (
            <DetailRow>
              <DetailLabel>Amount Paid</DetailLabel>
              <DetailValue>{formatCurrency(amount)}</DetailValue>
            </DetailRow>
          )}
          <DetailRow>
            <DetailLabel>Date</DetailLabel>
            <DetailValue>{new Date().toLocaleDateString()}</DetailValue>
          </DetailRow>
        </TransactionDetails>

        <ButtonGroup>
          <Button onClick={() => navigate('/purchase')}>
            View Purchases
          </Button>
          <PrimaryButton onClick={() => navigate('/book')}>
            Book a Class
          </PrimaryButton>
        </ButtonGroup>
      </SuccessContainer>
    </PageContainer>
  );
};

export default CheckoutSuccessPage;